import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Collapse, Text, TextInput, Textarea } from '@mantine/core';
import { campusById, serviceTimesFor } from '@/lib/lookups';
import { driverMissingStep } from '@/store/ridesWizard';
import { useDriverProfile } from '@/lib/me';
import { formatTime } from '@/lib/format';
import { useCopy, seatRange } from '@/lib/settings';
import { StepHeader } from '@/components/ui/StepHeader';
import { StickyActionBar } from '@/components/ui/StickyActionBar';
import { LoadingState } from '@/components/ui/LoadingState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Callout } from '@/components/ui/Callout';
import { CampusPicker } from '@/components/rides/CampusPicker';
import { ServiceTimePicker } from '@/components/rides/ServiceTimePicker';
import { defaultDeparture, driverNeedsService, useOfferFlow } from './_shared';
import '@/styles/flows.css';

type Part = 'from' | 'to' | 'service' | 'details';

/**
 * Offer a lift, the route, one question per screen: pick-up campus (?q=from), drop-off campus
 * (?q=to), the service for Sunday services (?q=service), then seats, time and a note (?q=details).
 * The departure time shows the default riders will see when it is left blank.
 */
export default function DriveRoutePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { t } = useCopy();
  const f = useOfferFlow();
  const { driver, setDriver, lookups, settings, chosen, drop, svc } = f;
  const profile = useDriverProfile();
  const [customTime, setCustomTime] = useState<boolean>(!!driver.departureTime);
  const q = params.get('q');
  const part: Part = q === 'to' || q === 'service' || q === 'details' ? q : 'from';
  // Never offer more seats than the car has.
  const carSeats = profile.data?.vehicle.seats;
  useEffect(() => {
    if (carSeats && driver.seats > carSeats) setDriver({ seats: carSeats });
  }, [carSeats, driver.seats, setDriver]);

  const missing = driverMissingStep(driver, 'route');
  if (missing) return <Navigate to={missing} replace />;
  if (part !== 'from' && driver.collectionLocationId == null) return <Navigate to="/drive/route" replace />;
  if ((part === 'service' || part === 'details') && driver.dropOffLocationId == null) return <Navigate to="/drive/route?q=to" replace />;
  if (part === 'service' && lookups.data && f.events.data && !f.needsService) return <Navigate to="/drive/route?q=details" replace />;

  const seatChoices = seatRange(settings, profile.data?.vehicle.seats);
  const times = serviceTimesFor(drop);
  const afterTo = (dropId: number) => (driverNeedsService(chosen, campusById(lookups.data, dropId)) ? '/drive/route?q=service' : '/drive/route?q=details');
  const serviceMissing = f.timeRequired && times.length > 0 && driver.serviceTimeId == null;

  const pickFrom = (id: number) => {
    setDriver({ collectionLocationId: id, dropOffLocationId: driver.dropOffLocationId === id ? null : driver.dropOffLocationId });
    navigate('/drive/route?q=to');
  };
  const pickTo = (id: number) => {
    if (id !== driver.dropOffLocationId) setDriver({ dropOffLocationId: id, serviceTimeId: null });
    navigate(afterTo(id));
  };
  const pickService = (id: number | null) => {
    setDriver({ serviceTimeId: id });
    navigate('/drive/route?q=details');
  };

  const def = defaultDeparture(chosen, svc?.startTime ?? null, settings);
  const lead = settings.defaultDepartureLeadMinutes;
  const seatsOk = driver.seats >= seatChoices[0] && driver.seats <= seatChoices[seatChoices.length - 1];

  const head = {
    from: { step: f.stepOf('from'), title: 'Where will you pick up riders?', lede: t('drive.from_intro'), backTo: '/drive/events' },
    to: { step: f.stepOf('to'), title: 'Which campus are you driving to?', lede: t('drive.to_intro'), backTo: '/drive/route' },
    service: { step: f.stepOf('service'), title: 'Which service are you going to?', lede: t('drive.service_intro', undefined, { campus: drop?.name ?? 'this campus' }), backTo: '/drive/route?q=to' },
    details: { step: f.stepOf('details'), title: 'Seats and time', lede: t('drive.details_intro'), backTo: f.needsService ? '/drive/route?q=service' : '/drive/route?q=to' },
  }[part];

  let action: { label: string; go: () => void } | null = null;
  if (part === 'from' && driver.collectionLocationId != null) action = { label: 'Continue', go: () => navigate('/drive/route?q=to') };
  if (part === 'to' && driver.dropOffLocationId != null && driver.dropOffLocationId !== driver.collectionLocationId) action = { label: 'Continue', go: () => navigate(afterTo(driver.dropOffLocationId!)) };
  if (part === 'service' && !serviceMissing) action = { label: 'Continue', go: () => navigate('/drive/route?q=details') };
  if (part === 'details') action = { label: 'Check your lift', go: () => (seatsOk ? navigate('/drive/summary') : undefined) };

  return (
    <div className="rides-main rides-flow">
      <StepHeader step={head.step} total={f.total} title={head.title} lede={head.lede} backTo={head.backTo} chips={f.chips(part)} />
      <div className="rides-stepbody" key={part}>
      {lookups.isLoading && <LoadingState label="Loading campuses" count={3} />}
      {lookups.error && <ErrorState title="We couldn't load the campuses" error={lookups.error} onRetry={() => lookups.refetch()} retrying={lookups.isFetching} />}

      {lookups.data && part === 'from' && (
        <CampusPicker campuses={lookups.data.campuses} value={driver.collectionLocationId} onChange={pickFrom} ariaLabel="Pick-up campus" navigates />
      )}
      {lookups.data && part === 'to' && (
        <CampusPicker campuses={lookups.data.campuses} value={driver.dropOffLocationId} onChange={pickTo} excludeId={driver.collectionLocationId} hideExcluded ariaLabel="Drop-off campus" navigates />
      )}
      {lookups.data && part === 'service' && (
        <ServiceTimePicker
          variant="cards"
          navigates
          times={times}
          value={driver.serviceTimeId}
          onChange={pickService}
          allowAny={!f.timeRequired}
          anyLabel="Not sure yet"
          anyDescription="Riders see your lift for any service"
          campusName={drop?.name}
        />
      )}

      {lookups.data && part === 'details' && (
        <div className="rides-flow-stack">
          <section>
            <h2 className="rides-sublabel" id="seats-label">How many riders can you take?</h2>
            <div className="rides-seatrow" style={{ ['--seat-count' as string]: Math.min(seatChoices.length, 8) }} role="radiogroup" aria-labelledby="seats-label">
              {seatChoices.map((n) => (
                <button key={n} type="button" role="radio" className="rides-chip" aria-checked={driver.seats === n} onClick={() => setDriver({ seats: n })}>{n}</button>
              ))}
            </div>
            {profile.data?.vehicle.seats ? <p className="rides-help" style={{ marginTop: 8 }}>Your car has {profile.data.vehicle.seats} passenger {profile.data.vehicle.seats === 1 ? 'seat' : 'seats'}.</p> : null}
          </section>

          <section>
            <h2 className="rides-sublabel">When do you leave {f.col?.name ?? 'the campus'}?</h2>
            <div className="rides-card" style={{ padding: 16 }}>
              {def ? (
                <Text>Riders will see <b>{customTime && driver.departureTime ? formatTime(driver.departureTime) : def}</b>.</Text>
              ) : (
                <Text>{customTime && driver.departureTime ? <>Riders will see <b>{formatTime(driver.departureTime)}</b>.</> : t('drive.default_time_each', undefined, { minutes: lead })}</Text>
              )}
              {def && !customTime && <p className="rides-help" style={{ marginTop: 4 }}>{t('drive.default_time', undefined, { time: def, minutes: lead })}</p>}
              <button
                type="button"
                className="rides-linkbtn"
                aria-expanded={customTime}
                onClick={() => { if (customTime) setDriver({ departureTime: null }); setCustomTime((v) => !v); }}
              >
                {customTime ? 'Use the usual time' : 'Choose a different time'}
              </button>
              <Collapse in={customTime}>
                <TextInput
                  type="time"
                  label="Leaving time"
                  value={driver.departureTime ?? ''}
                  onChange={(e) => setDriver({ departureTime: e.currentTarget.value || null })}
                  mt="xs"
                  styles={{ input: { maxWidth: 200 } }}
                />
              </Collapse>
            </div>
          </section>

          {settings.allowDriverNotes && (
            <Textarea
              label="Note for riders (optional)"
              description="For example, where you'll park or if you have a child seat."
              placeholder="For example, I park next to the security booth."
              value={driver.notes}
              onChange={(e) => setDriver({ notes: e.currentTarget.value })}
              autosize
              minRows={2}
              maxLength={200}
            />
          )}
          {!seatsOk && <Callout tone="bad">Please choose how many riders you can take.</Callout>}
        </div>
      )}

      </div>
      {lookups.data && action && (
        <StickyActionBar>
          <Button fullWidth onClick={action.go}>{action.label}</Button>
        </StickyActionBar>
      )}
    </div>
  );
}
